import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import '../styles/MyBookings.css';
import bookingService from '../services/bookingService';
import gamStar from '../assets/gam_star.png';

export default function MyBookings({ onNavigateToSignIn }) {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Fetch bookings on component mount
  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - fetchBookings doesn't need to be in deps

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError('');

      // Check if user is logged in
      const token = sessionStorage.getItem('authToken');
      if (!token) {
        navigate('/');
        return;
      }

      const response = await bookingService.getUserBookings();

      if (response.success) {
        setBookings(response.bookings || []);
      } else {
        setError(response.message || 'Failed to load bookings');
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'CONFIRMED':
        return 'status-confirmed';
      case 'PENDING':
        return 'status-pending';
      case 'COMPLETED':
        return 'status-completed';
      case 'CANCELLED':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const time = new Date(`2000-01-01T${timeString}`);
    return time.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="my-bookings-page">
      <Header activePage="services" onNavigateToSignIn={onNavigateToSignIn} />

      {/* Gam Star positioned below header */}
      <div className="decorative-star right-star">
        <img src={gamStar} alt="Decorative Star" className="gam-star" />
      </div>

      <main className="my-bookings-main">
        <div className="my-bookings-container">
          <h1 className="my-bookings-title">My Bookings</h1>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading your bookings...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p className="error-message">{error}</p>
              <button onClick={fetchBookings} className="retry-button">
                Retry
              </button>
            </div>
          ) : bookings.length === 0 ? (
            <div className="empty-state">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="empty-icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <h2>No bookings yet</h2>
              <p>You haven't made any consultation bookings yet.</p>
              <button
                onClick={() => navigate('/consultation')}
                className="book-now-button"
              >
                Book a Consultation
              </button>
            </div>
          ) : (
            <div className="bookings-grid">
              {bookings.map((booking) => (
                <div key={booking.id} className="booking-card">
                  <div className="booking-header">
                    <span className={`status-badge ${getStatusBadgeClass(booking.status)}`}>
                      {booking.status}
                    </span>
                    <span className="booking-id">#{booking.id}</span>
                  </div>

                  <div className="booking-content">
                    <div className="booking-section">
                      <h3 className="section-title">Consultation Details</h3>
                      <div className="detail-row">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="detail-icon"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <div>
                          <p className="detail-label">Date</p>
                          <p className="detail-value">{formatDate(booking.consultationDate)}</p>
                        </div>
                      </div>
                      <div className="detail-row">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="detail-icon"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div>
                          <p className="detail-label">Time Slot</p>
                          <p className="detail-value">{booking.consultationTimeSlot}</p>
                        </div>
                      </div>
                    </div>

                    <div className="booking-section">
                      <h3 className="section-title">Personal Information</h3>
                      <div className="info-grid">
                        <div>
                          <p className="info-label">Name</p>
                          <p className="info-value">{booking.name}</p>
                        </div>
                        <div>
                          <p className="info-label">Email</p>
                          <p className="info-value">{booking.email}</p>
                        </div>
                        <div>
                          <p className="info-label">Phone</p>
                          <p className="info-value">{booking.phone}</p>
                        </div>
                      </div>
                    </div>

                    <div className="booking-section">
                      <h3 className="section-title">Birth Details</h3>
                      <div className="info-grid">
                        <div>
                          <p className="info-label">Date of Birth</p>
                          <p className="info-value">{formatDate(booking.dateOfBirth)}</p>
                        </div>
                        {booking.timeOfBirth && (
                          <div>
                            <p className="info-label">Time of Birth</p>
                            <p className="info-value">{formatTime(booking.timeOfBirth)}</p>
                          </div>
                        )}
                        <div>
                          <p className="info-label">Place of Birth</p>
                          <p className="info-value">{booking.placeOfBirth}</p>
                        </div>
                      </div>
                      {booking.message && (
                        <div className="message-box">
                          <p className="info-label">Message</p>
                          <p className="info-value">{booking.message}</p>
                        </div>
                      )}
                    </div>

                    {booking.meetLink && booking.status !== 'CANCELLED' && (
                      <div className="booking-section meet-section">
                        <h3 className="section-title">Google Meet Link</h3>
                        <a
                          href={booking.meetLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="meet-link-button"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="meet-icon"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                          Join Google Meet
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="booking-footer">
                    <p className="booking-date-created">
                      Booked on {formatDate(booking.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
